import { type EditCommand, type Edl, type TrackKind, locateItem } from '@repo/editor-core';

/** Where the visible agent cursor should point for a given command. */
export interface CursorTarget {
  trackKind: TrackKind;
  frame: number;
  label: string;
}

const OP_LABEL: Record<EditCommand['op'], string> = {
  addClip: '클립 추가',
  split: '분할',
  trim: '트림',
  move: '이동',
  remove: '삭제',
  addTransition: '전환',
  addCaption: '자막 추가',
  editCaption: '자막 편집',
  setCrop: '크롭',
  setFit: '화면 맞춤',
  addGif: 'GIF 추가',
  addSfx: '효과음',
  setLayout: '레이아웃',
  setTitle: '제목',
};

function trackKindOf(trackId: string): TrackKind {
  if (trackId.startsWith('caption')) return 'caption';
  if (trackId.startsWith('sfx')) return 'sfx';
  if (trackId.startsWith('overlay')) return 'overlay';
  if (trackId.startsWith('broll')) return 'broll';
  return 'video';
}

/**
 * Resolve the timeline point a command acts on, so the cursor can travel there
 * before the edit is applied. Pure presentation — returns null when there is no
 * meaningful target (the edit still applies; it just isn't choreographed).
 */
export function computeTarget(cmd: EditCommand, edl: Edl): CursorTarget | null {
  const label = OP_LABEL[cmd.op];
  switch (cmd.op) {
    case 'addClip':
      return { trackKind: 'video', frame: cmd.at, label };
    case 'addCaption':
      return { trackKind: 'caption', frame: cmd.from, label };
    case 'addSfx':
      return { trackKind: 'sfx', frame: cmd.at, label };
    case 'addGif':
      return { trackKind: 'overlay', frame: cmd.at, label };
    case 'move':
      return { trackKind: trackKindOf(cmd.toTrackId), frame: cmd.at, label };
    case 'addTransition': {
      const loc = locateItem(edl, cmd.afterItemId);
      return loc ? { trackKind: loc.track.kind, frame: loc.item.from, label } : null;
    }
    case 'split':
      return locateTarget(edl, cmd.itemId, label, cmd.atFrame);
    case 'trim':
    case 'remove':
    case 'setCrop':
    case 'setFit':
    case 'editCaption':
      return locateTarget(edl, cmd.itemId, label);
    case 'setLayout':
    case 'setTitle':
      // Document-level edits — no single timeline point to choreograph to.
      return null;
  }
}

function locateTarget(
  edl: Edl,
  itemId: string,
  label: string,
  frame?: number
): CursorTarget | null {
  const loc = locateItem(edl, itemId);
  if (!loc) return null;
  return { trackKind: loc.track.kind, frame: frame ?? loc.item.from, label };
}

export function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}
